import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Flex from "../../../../../components/Flex";
import CustomTitle from "../../../../../components/app/CustomTitle";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { Box, Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import { FormBuilder } from "react-formio";
import CustomSelect from "../../../../../components/CustomSelect";
import Input from "../../../../../components/Input";
import { appPaths } from "../../../../../constants/paths";
import { useFormBuilderCreate } from "../../../../../queries/form-builder/use-form-builder-create";
import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
} from "../../../../../utils/utils";
import PreviewFormDrawer from "./PreviewFormDrawer";
import {
  FORM_INITIAL_VALUES,
  FORM_TYPE_LIST,
  FORM_TYPE_MAPPER,
  FormBuilderInitialValues,
  FORM_BUILDER_INIT_VALS,
  FORM_VALIDATION_SCHEMA,
} from "./form-builder-constant";
import "./style/form-builder.css";
import { cloneDeep } from "lodash";
import { useFormBuilderUpdate } from "../../../../../queries/form-builder/use-form-builder-update";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import { MaskMultiSelectScroll } from "../../../../../components/MaskMultiSelectScroll";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { ScrollArea } from "../../../../../components/ScrollBar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

interface FormBuilderHostProps {
  isLoading?: boolean;
  preFillSchema?: FormBuilderInitialValues;
  onClose?: () => void;
}

/**
 *
 * @returns host component for form builder
 * - which manage forms creation
 *
 * @author rajkumar rathod
 */
const FormBuilderHost = ({
  preFillSchema,
  onClose,
  isLoading,
}: FormBuilderHostProps) => {
  const formik = useFormik({
    initialValues: preFillSchema ?? FORM_INITIAL_VALUES,
    validationSchema: FORM_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const navigate = useNavigate();
  const isProvider = getViewAs() === "provider";
  const realm = getStoredRealm();

  const [specialtySearchKey, setSpecialtySearchKey] = useDebounce<string>("");
  const [specialtyPagination, setSpecialtyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: specialtiesList, isFetching: isSpecialtiesFetching } =
    useSpecialtiesList({
      searchString: specialtySearchKey,
      pagination: specialtyPagination,
    });

  const specialtyList = useMemo(
    () => specialtiesList?.data?.content ?? [],
    [specialtiesList]
  );

  const { mutateAsync: createForm } = useFormBuilderCreate();
  const { mutateAsync: updateForm } = useFormBuilderUpdate();

  const [schema, setSchema] = useState(
    preFillSchema || cloneDeep(FORM_BUILDER_INIT_VALS)
  );

  const previewFormDisclosure = useDisclosure(false);

  const showSnackbar = useSnackbar();

  useEffect(() => {
    setSchema((prev) => preFillSchema ?? prev);
  }, [preFillSchema]);

  function handleSubmit(values: FormBuilderInitialValues) {
    const payload = getUnNecessaryTransformData(["specialities"], {
      ...values,
      components: undefined,
      content:
        schema?.components && schema?.components?.length > 0
          ? JSON.stringify({ components: schema?.components ?? [] })
          : undefined,
    });

    const transformedPayload = {
      customForm: payload as any,
      xTENANTID: isProvider && realm ? realm : undefined,
    };

    if (!preFillSchema) showToastAndResetForm(createForm(transformedPayload));
    else showToastAndResetForm(updateForm(transformedPayload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        setSchema(cloneDeep(FORM_BUILDER_INIT_VALS));
        formik.setValues(cloneDeep(FORM_INITIAL_VALUES));
        formik.resetForm();
        showSnackbar(getSuccessSnackbar(res));
        setTimeout(handleNavigateBack, 1200);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function handleNavigateBack() {
    navigate(
      isProvider
        ? appPaths.libraryProviderFormBuilderPath
        : appPaths.libraryFormBuilderPath
    );
    onClose?.();
  }

  const shouldReduceHeight =
    (formik.touched?.formType && formik.errors?.formType) ||
    (formik.touched?.name && formik.errors?.name);

  return (
    <Fragment key={"form-builder-host-key"}>
      <LoadingOverlay isLoading={!!isLoading} />

      <Flex justifyContent={"space-between"} mb={"20px"}>
        <Flex gap={10}>
          <Flex
            gap={1}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            onClick={handleNavigateBack}
          >
            <ArrowBackIcon fontSize="medium" />
            <CustomTitle color={"black"}>Form Builder</CustomTitle>
          </Flex>
          <StyledButtonV2
            label="Preview"
            onClick={() => previewFormDisclosure.open()}
            variant="text"
            sIcon={<VisibilityIcon />}
          />
        </Flex>
      </Flex>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={3}>
          <CustomSelect
            label="Form Type"
            required
            value={formik.values?.formType}
            onChange={(val) => {
              formik.setFieldValue("formType", val);
            }}
            options={FORM_TYPE_LIST}
            error={formik.touched?.formType && formik.errors?.formType}
          />
        </Grid>
        <Grid item xs={3}>
          {/*  future reference while need to do -specialty singleSelect
          <CustomSelect
            label="Specialty"
            name="speciality"
            value={formik.values?.speciality}
            onChange={(val) => formik.setFieldValue("speciality", val)}
            options={specialtiesList?.data?.content?.map((specialty) => ({
              value: specialty?.uuid,
              label: specialty?.name,
            }))}
            error={formik.touched?.speciality && formik.errors?.speciality}
          /> */}
          <MaskMultiSelectScroll
            optionWithCheckbox
            // required
            label="Specialties"
            value={formik.values?.specialities as any}
            onChange={(val) => formik.setFieldValue("specialities", val)}
            options={specialtyList}
            onSearch={(v) => {
              setSpecialtySearchKey(v);
              setSpecialtyPagination((p) => ({ ...p, page: 0 }));
            }}
            pagination={specialtyPagination}
            setPagination={setSpecialtyPagination}
            totalElements={specialtiesList?.data?.page?.totalElements}
            loading={isSpecialtiesFetching}
            // error={formik.touched?.specialities && formik.errors?.specialities}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label="Name Of Form"
            required
            textFieldProps={{
              name: "name",
              value: formik.values.name,
              onChange: formik.handleChange,
              placeholder: "Enter Name of Form",
            }}
            error={formik.touched?.name && formik.errors?.name}
          />
        </Grid>
      </Grid>

      {/* main builder host component */}
      <ScrollArea
        height={`calc(100svh - ${shouldReduceHeight ? "307.5px" : "287px"})`}
      >
        <Box
          pr={"15px"}
          // sx={{ overflowY: "auto" }}
          mb={1}
        >
          <FormBuilder
            form={schema}
            options={{
              noDefaultSubmitButton: true,
              builder: {
                premium: { components: { form: false, unknown: false } },
              },
            }}
          />
        </Box>
      </ScrollArea>

      <Flex flex={1} justifyContent={"end"}>
        <Button variant="contained" onClick={formik.submitForm}>
          Save
        </Button>
      </Flex>

      <PreviewFormDrawer
        disClosureHost={previewFormDisclosure}
        title={
          FORM_TYPE_MAPPER[formik.values?.formType ?? ""] ?? "Preview Form"
        }
        schema={schema}
        onEdit={previewFormDisclosure.close}
      />
    </Fragment>
  );
};
export default FormBuilderHost;
